import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

/** BrowserRouter has no data-router blocker. Cover its navigator and native history. */
export function useAuthoringNavigationGuard(unsaved: boolean, message: string) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const state = useRef({ unsaved, message });
  state.current = { unsaved, message };
  useEffect(() => {
    const confirm = () => !state.current.unsaved || window.confirm(state.current.message);
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    navigator.push = (...args) => { if (confirm()) originalPush.apply(navigator, args); };
    navigator.replace = (...args) => { if (confirm()) originalReplace.apply(navigator, args); };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (state.current.unsaved) { event.preventDefault(); event.returnValue = ''; }
    };
    let index = window.history.state?.idx as number | undefined;
    let restoring = false;
    const pop = (event: PopStateEvent) => {
      const nextIndex = event.state?.idx as number | undefined;
      if (restoring) { restoring = false; event.stopImmediatePropagation(); return; }
      if (state.current.unsaved && index !== undefined && nextIndex !== undefined && !confirm()) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(index - nextIndex);
      } else index = nextIndex;
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('popstate', pop, true);
    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('popstate', pop, true);
    };
  }, [navigator]);
}
